import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  QrCodeScanner,
  Keyboard,
  CheckCircle,
  Cancel,
  Warning,
  Event,
  Person,
  ConfirmationNumber,
} from '@mui/icons-material';
import Header from '../Header';
import { getTicketApiBase } from '../../../utils/ticketApiBase';

const API_BASE = () => getTicketApiBase();

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const DetailRow = ({ label, value, mono, accent }) => {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.85, gap: 2 }}>
      <Typography variant="body2" sx={{ color: '#888', flexShrink: 0 }}>{label}</Typography>
      <Typography
        variant="body2"
        sx={{
          color: accent ? '#ffa11f' : '#fff',
          fontWeight: accent ? 700 : 500,
          fontFamily: mono ? 'monospace' : 'inherit',
          textAlign: 'right',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const SectionCard = ({ title, icon, children }) => (
  <Paper sx={{ p: 2, mb: 2, backgroundColor: '#121210', border: '1px solid #333', borderRadius: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      {icon}
      <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700 }}>{title}</Typography>
    </Box>
    {children}
  </Paper>
);

const CheckInDetails = ({ result }) => (
  <Box sx={{ mt: 2, textAlign: 'left' }}>
    <SectionCard title="Event" icon={<Event sx={{ color: '#667eea', fontSize: 18 }} />}>
      {/* <DetailRow label="Event Title" value={result.eventTitle} /> */}
      <DetailRow label="Event Name" value={result.eventName} accent />
      {/* <DetailRow label="Date & Time" value={formatDate(result.eventDate)} /> */}
      {/* <DetailRow label="Venue" value={result.venue} /> */}
      {/* {result.venueLocationLink && (
        <Box sx={{ mt: 0.5 }}>
          <Link
            href={result.venueLocationLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: '#ffa11f', fontSize: 13 }}
          >
            View venue on map
          </Link>
        </Box>
      )} */}
    </SectionCard>

    <SectionCard title="Guest" icon={<Person sx={{ color: '#667eea', fontSize: 18 }} />}>
      <DetailRow label="Name" value={result.name} accent />
      <DetailRow label="Email" value={result.email} />
    </SectionCard>

    <SectionCard title="Ticket" icon={<ConfirmationNumber sx={{ color: '#667eea', fontSize: 18 }} />}>
      {result.parentTicketId && result.parentTicketId !== result.ticketId && (
        <DetailRow label="Order ID" value={result.parentTicketId} mono />
      )}
      <DetailRow label="Ticket ID" value={result.ticketId} mono accent />
      {/* <DetailRow label="Verification Code" value={result.verificationCode} mono accent /> */}
      {result.ticketType ? (
        <DetailRow label="Ticket Type" value={result.ticketType} accent />
      ) : (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#888', mb: 0.75 }}>Ticket Types</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end' }}>
            {(result.ticketTypes || []).map((type) => (
              <Chip
                key={type}
                label={type}
                size="small"
                sx={{ backgroundColor: 'rgba(255,161,31,0.15)', color: '#ffa11f', fontWeight: 600 }}
              />
            ))}
          </Box>
        </Box>
      )}
      {result.entryIndex != null && (
        <DetailRow label="Ticket #" value={`${result.entryIndex} of ${result.orderQuantity}`} />
      )}
      {!result.isIndividualTicket && result.quantity > 1 && (
        <DetailRow label="Admits" value={`${result.quantity} people`} />
      )}
      {result.checkedInAt && (
        <DetailRow label="Checked In At" value={formatDate(result.checkedInAt)} />
      )}
    </SectionCard>

    {/* <SectionCard title="Payment" icon={<CreditCard sx={{ color: '#667eea', fontSize: 18 }} />}>
      <DetailRow label="Amount Paid" value={result.amountPaidFormatted} accent />
      {result.couponCode && <DetailRow label="Coupon" value={result.couponCode} mono />}
      {(result.cardBrand || result.cardLast4) && (
        <DetailRow
          label="Card"
          value={`${result.cardBrand || 'Card'}${result.cardLast4 ? ` •••• ${result.cardLast4}` : ''}`}
        />
      )}
      <DetailRow label="Payment Status" value={result.paymentStatus} />
      <DetailRow label="Purchased" value={formatDate(result.purchasedAt)} />
    </SectionCard> */}
  </Box>
);

const AdminCheckIn = ({ embedded = false }) => {
  const [tab, setTab] = useState(0);
  const [ticketId, setTicketId] = useState('');
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const handleVerify = async (tId, vCode) => {
    setResult(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE()}/api/tickets/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticketId: tId || ticketId, code: vCode || code }),
      });
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setResult({ valid: false, message: 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  const startScanner = async () => {
    setScanning(true);
    setResult(null);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          try {
            const parsed = JSON.parse(decodedText);
            if (parsed.ticketId && parsed.code) {
              stopScanner();
              setTicketId(parsed.ticketId);
              setCode(parsed.code);
              handleVerify(parsed.ticketId, parsed.code);
            }
          } catch (e) {
            // Not valid JSON QR
          }
        },
        () => {}
      );
    } catch (e) {
      console.error('Scanner error:', e);
      setScanning(false);
    }
  };

  const stopScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().then(() => {
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      }).catch(() => {});
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  const hasDetails = result && (result.eventTitle || result.name || result.ticketId);

  const renderResult = () => {
    if (!result) return null;

    if (result.valid) {
      return (
        <Paper sx={{ p: 3, mt: 3, backgroundColor: 'rgba(76,175,80,0.08)', border: '2px solid #4caf50', borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: hasDetails ? 1 : 0 }}>
            <CheckCircle sx={{ fontSize: 56, color: '#4caf50', mb: 1 }} />
            <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 700 }}>
              Checked In
            </Typography>
            <Typography variant="body1" sx={{ color: '#fff', mt: 0.5, fontWeight: 600 }}>
              Welcome, {result.name}
            </Typography>
            {result.ticketType && (
              <Chip
                label={result.ticketType}
                size="small"
                sx={{ mt: 1.5, backgroundColor: 'rgba(255,161,31,0.2)', color: '#ffa11f', fontWeight: 700 }}
              />
            )}
          </Box>
          {hasDetails && <CheckInDetails result={result} />}
        </Paper>
      );
    }

    const isAlreadyUsed = result.alreadyCheckedIn || result.message?.toLowerCase().includes('already');
    const borderColor = isAlreadyUsed ? '#ff9800' : '#f44336';
    const iconColor = isAlreadyUsed ? '#ff9800' : '#f44336';

    return (
      <Paper sx={{ p: 3, mt: 3, backgroundColor: isAlreadyUsed ? 'rgba(255,152,0,0.08)' : 'rgba(244,67,54,0.08)', border: `2px solid ${borderColor}`, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: hasDetails ? 1 : 0 }}>
          {isAlreadyUsed ? (
            <Warning sx={{ fontSize: 56, color: iconColor, mb: 1 }} />
          ) : (
            <Cancel sx={{ fontSize: 56, color: iconColor, mb: 1 }} />
          )}
          <Typography variant="h5" sx={{ color: iconColor, fontWeight: 700 }}>
            {isAlreadyUsed ? 'Already Checked In' : 'Invalid Ticket'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
            {result.message}
          </Typography>
        </Box>
        {hasDetails && isAlreadyUsed && <CheckInDetails result={result} />}
      </Paper>
    );
  };

  const content = (
    <Box sx={{ maxWidth: 560, mx: 'auto', p: embedded ? 0 : 3 }}>
      <Tabs
        value={tab}
        onChange={(e, v) => { setTab(v); setResult(null); stopScanner(); }}
        sx={{ mb: 3, '& .MuiTab-root': { color: '#999' }, '& .Mui-selected': { color: '#667eea' } }}
        TabIndicatorProps={{ sx: { backgroundColor: '#667eea' } }}
      >
        <Tab icon={<QrCodeScanner />} label="Scan QR" />
        <Tab icon={<Keyboard />} label="Manual Entry" />
      </Tabs>

      {tab === 0 && (
        <Box>
          {!scanning ? (
            <Button
              fullWidth
              variant="contained"
              onClick={startScanner}
              startIcon={<QrCodeScanner />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                py: 2,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Start Scanning
            </Button>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              onClick={stopScanner}
              sx={{ mb: 2, color: '#f44336', borderColor: '#f44336' }}
            >
              Stop Scanner
            </Button>
          )}
          <Box
            id="qr-reader"
            ref={scannerRef}
            sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}
          />
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <TextField
            fullWidth
            label="Ticket ID"
            placeholder="TKT-XXXXXXXX"
            value={ticketId}
            onChange={(e) => setTicketId(e.target.value.toUpperCase())}
            sx={{ mb: 2 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff', fontFamily: 'monospace', fontSize: '1.1rem' } }}
          />
          <TextField
            fullWidth
            label="Verification Code"
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            sx={{ mb: 3 }}
            inputProps={{ maxLength: 6 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff', fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 4 } }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleVerify()}
            disabled={loading || !ticketId || !code}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              py: 1.5,
              fontWeight: 700,
            }}
          >
            {loading ? 'Verifying...' : 'Verify & Check In'}
          </Button>
        </Box>
      )}

      {renderResult()}
    </Box>
  );

  if (embedded) return content;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#121210' }}>
      <Header title="Ticket Check-In" />
      {content}
    </Box>
  );
};

export default AdminCheckIn;
